import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField,
  Stack,
  Typography,
  Alert,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { mapCompanyToForm } from '../../utils/settingsForm';
import { formatCheckExchangeRateLines } from '../../utils/checkDisplay';
import {
  buildCheckExchangeRateRows,
  buildCheckExchangeRatesPayload,
  getCheckConversionCurrencies,
  updateCurrencyRate,
} from '../../utils/checkExchangeRatesForm';

const cardPadding = { p: 1.5, '&:last-child': { pb: 1.5 } };

const rateFieldSx = {
  width: 64,
  flexShrink: 0,
  '& .MuiInputBase-root': { height: 32 },
  '& .MuiInputBase-input': { py: '4px', px: '6px', textAlign: 'center' },
  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
};

function ExchangeRateCardLabel({ checkCurrency, t, capHint = null }) {
  const tooltipTitle = capHint
    ? `${t('checks.exchangeRatesHelp', { currency: checkCurrency })} ${capHint}`
    : t('checks.exchangeRatesHelp', { currency: checkCurrency });
  return (
    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mb: 0.25 }}>
      <Typography variant="caption" color="text.secondary">
        {t('checks.exchangeRates')}
      </Typography>
      <Tooltip title={tooltipTitle} arrow placement="top">
        <IconButton size="small" sx={{ p: 0.125 }} aria-label={tooltipTitle}>
          <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function CheckExchangeRatesEditor({
  checkId,
  checkCurrency,
  defaultItemCurrency,
  exchangeRates,
  items = [],
  readOnly = false,
  inline = false,
  asCard = false,
  gridXs = 3,
  maxChangeAmount = null,
  onSaved,
}) {
  const { t } = useTranslation();
  const { company } = useAuth();
  const [rates, setRates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const requiredCurrencies = useMemo(
    () => getCheckConversionCurrencies(items, checkCurrency, defaultItemCurrency),
    [items, checkCurrency, defaultItemCurrency],
  );

  useEffect(() => {
    let cancelled = false;

    async function initRates() {
      setError('');
      if (requiredCurrencies.length === 0) {
        if (!cancelled) setRates([]);
        return;
      }

      const storedRates = exchangeRates?.length > 0 ? exchangeRates : [];
      if (storedRates.length > 0) {
        if (!cancelled) {
          setRates(buildCheckExchangeRateRows(
            requiredCurrencies,
            checkCurrency,
            storedRates,
          ));
        }
        return;
      }

      const companyId = company?.companyId;
      if (!companyId) {
        if (!cancelled) {
          setRates(buildCheckExchangeRateRows(requiredCurrencies, checkCurrency));
        }
        return;
      }

      try {
        const res = await axios.get(`/api/companies/${companyId}`);
        const mapped = mapCompanyToForm(res.data);
        const companyRows = (mapped.currencyRates || [])
          .filter((row) => row.toCurrency === checkCurrency);
        if (!cancelled) {
          setRates(buildCheckExchangeRateRows(
            requiredCurrencies,
            checkCurrency,
            [],
            companyRows,
          ));
        }
      } catch {
        if (!cancelled) {
          setRates(buildCheckExchangeRateRows(requiredCurrencies, checkCurrency));
        }
      }
    }

    initRates();
    return () => { cancelled = true; };
  }, [checkId, exchangeRates, checkCurrency, company?.companyId, defaultItemCurrency, requiredCurrencies]);

  const updateRate = (index, key, value) => {
    setRates((prev) => updateCurrencyRate(prev, index, key, value));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await axios.patch(
        `/api/checks/${checkId}/exchange-rates`,
        buildCheckExchangeRatesPayload(rates, checkCurrency),
      );
      onSaved?.(res.data);
    } catch (err) {
      setError(err.response?.data?.error || t('checks.exchangeRatesSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const filteredExchangeRates = (exchangeRates?.length > 0 ? exchangeRates : rates)
    .filter((row) => requiredCurrencies.includes(row.fromCurrency));

  const readOnlyLines = formatCheckExchangeRateLines(filteredExchangeRates, checkCurrency, t);

  const canSaveRates = rates.length > 0 && rates.every((row) => Number(row.rate) > 0);

  const saveButton = (
    <Tooltip title={t('checks.saveExchangeRates')}>
      <span>
        <IconButton
          color="primary"
          size="small"
          onClick={handleSave}
          disabled={saving || !canSaveRates}
          aria-label={t('checks.saveExchangeRates')}
          sx={{
            width: 28,
            height: 28,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
          }}
        >
          {saving ? <CircularProgress size={14} color="inherit" /> : <CheckIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </span>
    </Tooltip>
  );

  if (asCard) {
    const capHint = !readOnly && maxChangeAmount != null
      ? t('checks.exchangeRateCapHelp', { amount: maxChangeAmount })
      : null;
    return (
      <>
        {error && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ py: 0 }} onClose={() => setError('')}>
              {error}
            </Alert>
          </Grid>
        )}
        {readOnly ? (
          readOnlyLines.map((line) => (
            <Grid item xs={6} sm={gridXs} md={gridXs} key={line}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={cardPadding}>
                  <ExchangeRateCardLabel checkCurrency={checkCurrency} t={t} />
                  <Typography variant="body1" fontWeight="bold" color="primary.main">
                    {line}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          rates.map((row, index) => (
            <Grid item xs={6} sm={gridXs} md={gridXs} key={row.fromCurrency}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={cardPadding}>
                  <ExchangeRateCardLabel checkCurrency={checkCurrency} t={t} capHint={capHint} />
                  <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="nowrap">
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ fontSize: '0.7rem', lineHeight: 1.2, flexShrink: 0 }}
                    >
                      {row.fromCurrency} → {checkCurrency}
                    </Typography>
                    <TextField
                      size="small"
                      label={t('settings.rate')}
                      type="number"
                      inputProps={{ min: 0, step: 0.0001 }}
                      value={row.rate ?? ''}
                      onChange={(e) => updateRate(index, 'rate', e.target.value)}
                      sx={rateFieldSx}
                    />
                    {index === rates.length - 1 && saveButton}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </>
    );
  }

  const titleRow = (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: inline ? 1 : 0.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {t('checks.exchangeRates')}
      </Typography>
      <Tooltip title={t('checks.exchangeRatesHelp', { currency: checkCurrency })} arrow placement="top">
        <IconButton size="small" sx={{ p: 0.25 }} aria-label={t('checks.exchangeRatesHelp', { currency: checkCurrency })}>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const editorBody = readOnly ? (
    readOnlyLines.length > 0 ? (
      <Stack spacing={0.25}>
        {readOnlyLines.map((line) => (
          <Typography key={line} variant="body2" fontWeight={500}>{line}</Typography>
        ))}
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary">{t('checks.exchangeRatesEmpty')}</Typography>
    )
  ) : (
    <Stack spacing={0.75}>
      {rates.map((row, index) => (
        <Stack key={row.fromCurrency} direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {row.fromCurrency} → {checkCurrency}
          </Typography>
          <TextField
            size="small"
            label={t('settings.rate')}
            type="number"
            inputProps={{ min: 0, step: 0.0001 }}
            value={row.rate ?? ''}
            onChange={(e) => updateRate(index, 'rate', e.target.value)}
            sx={rateFieldSx}
          />
          {index === rates.length - 1 && saveButton}
        </Stack>
      ))}
    </Stack>
  );

  const capHint = !readOnly && maxChangeAmount != null && (
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
      {t('checks.exchangeRateCapHelp', { amount: maxChangeAmount })}
    </Typography>
  );

  if (inline) {
    return (
      <Box sx={{ width: '100%' }}>
        {titleRow}
        {capHint}
        {error && (
          <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {editorBody}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, mb: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      {titleRow}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('checks.exchangeRatesHelp', { currency: checkCurrency })}
      </Typography>
      {capHint}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {editorBody}
    </Box>
  );
}

export default CheckExchangeRatesEditor;
